import React, { useEffect, useMemo } from 'react';
import { Set } from 'immutable';
import Section from '@components/Section';
import { useDispatch, useSelector } from 'react-redux';
import {
  contactNotesSelector,
  currentlyEditingNoteSelector,
  deleteNote,
  setCurrentlyEditingNote,
} from '@actions/notes';
import { ID } from '@app/types';
import { creatingNewNoteInContactViewSelector, setCreatingNewNoteInContactView } from '@actions/ui';
import Contact, { RelatedContact } from '@models/Contact';
import Note from '@models/Note';
import TimelineNote, { NewTimelineNote } from '@components/TimelineNote';
import { TimelineItem, TimelineFilter } from '@models/Timeline';
import {
  selectContactTimeline,
  selectContactTimelineWindowSummary,
  selectFilteredContactTimeline,
} from '@reducers/data/timeline';
import TimelineInteraction from './Interaction';
import { ChevronDownIcon, ChevronUpDownIcon, LoaderIcon } from '@components/Icons';
import styles from './Timeline.module.scss';
import { fetchContactTimelineWindow, fetchFilteredContactTimeline } from '@actions/timeline';
import { pluralize } from '@support/string';

const ExpandWindowToggle = ({ contact }: { contact: Contact }) => {
  const dispatch = useDispatch();
  const timeline = useSelector(selectContactTimeline(contact.id));
  const windowSummary = useSelector(selectContactTimelineWindowSummary(contact.id));
  const { events, emails, calls, notes, total } = windowSummary || {};
  const shouldRender = windowSummary && total > 0;
  const loading = timeline?.isFetching || windowSummary?.isFetching;

  return shouldRender ? (
    <button
      className={styles.expandBtn}
      onClick={() => dispatch(fetchContactTimelineWindow({ contactID: contact.id }))}
      disabled={loading}
    >
      {loading ? (
        <LoaderIcon />
      ) : (
        <div className={styles.expandIcon}>
          <ChevronUpDownIcon />
        </div>
      )}
      <div className={styles.summary}>
        {(events || 0) > 0 && (
          <span>
            {events} {pluralize(events, 'Event')}
          </span>
        )}
        {(emails || 0) > 0 && (
          <span>
            {emails} {pluralize(events, 'Email')}
          </span>
        )}
        {(notes || 0) > 0 && (
          <span>
            {notes} {pluralize(events, 'Note')}
          </span>
        )}
        {(calls || 0) > 0 && (
          <span>
            {calls} {pluralize(calls, 'Call')}
          </span>
        )}
      </div>
    </button>
  ) : null;
};

const TimelineItemsList = ({
  contact,
  timelineItems,
  showWindowButtonAfterID,
  currentlyEditingNote,
  clearNewNote,
  deleteNote,
  setCurrentlyEditing,
  clearCurrentlyEditing,
  filterApplied,
  loadMoreFilteredTimeline,
  loadingFilteredTimeline,
}: {
  contact: Contact;
  timelineItems: TimelineItem[];
  showWindowButtonAfterID?: ID;
  currentlyEditingNote?: Note;
  clearNewNote?: (note: Note) => void;
  deleteNote?: (note: Note) => void;
  setCurrentlyEditing?: (note: Note, e: any) => void;
  clearCurrentlyEditing?: () => void;
  filterApplied: boolean;
  loadMoreFilteredTimeline?: () => void;
  loadingFilteredTimeline?: boolean;
}) => {
  const emptyTimeline = timelineItems.length === 0;

  return (
    <Section style={{ marginTop: 32 }}>
      {!filterApplied && (
        <NewTimelineNote clearNewNote={clearNewNote} emptyTimeline={emptyTimeline} />
      )}
      {timelineItems.map((timelineItem, idx, timeline) => {
        const isLast = idx === timeline.length - 1;
        const includeWindowButton = !filterApplied && timelineItem.id === showWindowButtonAfterID;
        let renderedItem = null;

        if (timelineItem.type === 'note') {
          renderedItem = (
            <TimelineNote
              noteID={timelineItem.noteID}
              currentlyEditingNote={currentlyEditingNote}
              setCurrentlyEditing={setCurrentlyEditing}
              clearCurrentlyEditing={clearCurrentlyEditing}
              deleteNote={deleteNote}
              user={timelineItem.user}
              isLast={isLast}
            />
          );
        } else {
          renderedItem = (
            <TimelineInteraction contact={contact} timelineItem={timelineItem} isLast={isLast} />
          );
        }

        return (
          <React.Fragment key={`${timelineItem.stableID}_${String(includeWindowButton)}`}>
            {renderedItem}
            {includeWindowButton && <ExpandWindowToggle contact={contact} />}
          </React.Fragment>
        );
      })}
      {loadMoreFilteredTimeline && (
        <button
          className={styles.expandBtn}
          onClick={loadMoreFilteredTimeline}
          disabled={loadingFilteredTimeline}
        >
          {loadingFilteredTimeline ? (
            <LoaderIcon />
          ) : (
            <div className={styles.expandIcon}>
              <ChevronDownIcon width={7} height={7} color="textSecondary" />
            </div>
          )}
          <div className={styles.summary}>Show more</div>
        </button>
      )}
    </Section>
  );
};

export default function Timeline(props: {
  contact: Contact;
  relatedContacts: RelatedContact[];
  setRelatedContacts: (relatedContacts: RelatedContact[]) => void;
  filterValue: TimelineFilter | null;
}) {
  const dispatch = useDispatch();
  const notes = useSelector(contactNotesSelector(props.contact.id));
  const timeline = useSelector(selectContactTimeline(props.contact.id));
  const filteredTimeline = useSelector(
    selectFilteredContactTimeline(props.contact.id, props.filterValue),
  );
  const currentlyEditingNote = useSelector(currentlyEditingNoteSelector);
  const creatingNewNote = useSelector(creatingNewNoteInContactViewSelector);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    if (props.filterValue) {
      return filteredTimeline?.items?.valueSeq().toArray() || [];
    }

    return timeline.items.valueSeq().toArray();
  }, [timeline, filteredTimeline, props.filterValue]);

  const relatedContactIDs = useMemo<Set<ID>>(
    () => Set(props.relatedContacts.map(({ id }) => id)),
    [props.relatedContacts],
  );

  const externallyRelatedContactIDs = useMemo<Set<ID>>(
    () =>
      Set(props.relatedContacts.filter(({ bidirectional }) => bidirectional).map(({ id }) => id)),
    [props.relatedContacts],
  );

  // Set of the contact mention IDs in all of the available notes except the note that is currently being
  // edited. If we are editing a persisted note, we are operating on a copy as currentlyEditingNote -- we filter
  // the original out of notes so that we can accurately determine if the full set of mentions has changed
  // when a user edits the currentlyEditingNote's mentions.
  const mentionsIDsInReadOnlyNotes = useMemo<Set<ID>>(() => {
    const filteredNotes = currentlyEditingNote
      ? notes.filter((note) => note.id != currentlyEditingNote.id)
      : notes;
    return filteredNotes.reduce((ids, note) => ids.concat(note.mentions), Set<ID>()).toSet();
  }, [notes, currentlyEditingNote?.id]);

  const setRelatedContactsIfChanged = (mentionIDs: Set<ID>) => {
    // We add the related contacts from elsewhere when determining the new full set
    // of related contacts when a note has been edited
    const combinedMentionIDs = mentionIDs.union(externallyRelatedContactIDs);

    if (!relatedContactIDs.equals(combinedMentionIDs)) {
      const addedMentions = combinedMentionIDs
        .subtract(relatedContactIDs)
        .valueSeq()
        .toArray()
        .map((id) => ({ id } as RelatedContact));
      // filter out deletions
      const newRelatedContacts = props.relatedContacts
        .filter(({ id }) => combinedMentionIDs.has(id))
        .concat(addedMentions);
      props.setRelatedContacts(newRelatedContacts);
    }
  };

  useEffect(() => {
    if (currentlyEditingNote) {
      const mentionsIDsInNotes = Set<ID>(
        mentionsIDsInReadOnlyNotes.concat(currentlyEditingNote.mentions),
      );
      setRelatedContactsIfChanged(mentionsIDsInNotes);
    }
  }, [currentlyEditingNote]);

  // If a note is deleted, reset the mentions for this Contact if needed
  useEffect(() => {
    if (props.contact.updated) {
      setRelatedContactsIfChanged(mentionsIDsInReadOnlyNotes);
    }
  }, [notes.length]);

  useEffect(() => {
    if (!currentlyEditingNote && creatingNewNote) {
      dispatch(setCreatingNewNoteInContactView(false));
    }
  }, [currentlyEditingNote]);

  useEffect(() => {
    if (props.filterValue && !filteredTimeline) {
      dispatch(
        fetchFilteredContactTimeline({
          contactID: props.contact.id,
          filterType: props.filterValue,
        }),
      );
    }
  }, [props.filterValue]);

  // when component unmounts, ensure we clear the currentlyEditing state
  useEffect(
    () => () => {
      dispatch(setCurrentlyEditingNote(null));
    },
    [],
  );

  // selection for editing
  const setCurrentlyEditing = (note, e) => {
    if (e) {
      e.preventDefault();
      // had to add this because of (in theory) changes in focus/blur in React 17.
      // the blur() even was being called immediately after, resulting in the note
      // never being editable.
      e.stopPropagation();
    }

    // If a user clicks straight from a focused new note to an existing note,
    // the blur handler won't get called (due to the e.stopPropagation above).
    // If that happens, we need to manually clear the new note state to prevent
    // a duplicate note appearing.
    if (note?.isPersisted && creatingNewNote) {
      dispatch(setCreatingNewNoteInContactView(false));
    }

    dispatch(setCurrentlyEditingNote(note));
  };

  const clearCurrentlyEditing = () => setCurrentlyEditing(null, null);

  const clearNewNote = (note: Note) => {
    // if the new note is persisted AND it is empty, delete it here
    if (note.isPersisted && note.isEmpty) {
      dispatch(deleteNote(note));
    }

    dispatch(setCreatingNewNoteInContactView(false));
    clearCurrentlyEditing();
  };

  const loadMoreFilteredTimeline = () => {
    dispatch(
      fetchFilteredContactTimeline({ contactID: props.contact.id, filterType: props.filterValue }),
    );
  };

  return (
    <TimelineItemsList
      contact={props.contact}
      timelineItems={timelineItems}
      showWindowButtonAfterID={timeline.showBtnAfterID}
      currentlyEditingNote={currentlyEditingNote}
      clearNewNote={clearNewNote}
      deleteNote={(note) => dispatch(deleteNote(note))}
      setCurrentlyEditing={setCurrentlyEditing}
      clearCurrentlyEditing={clearCurrentlyEditing}
      filterApplied={!!props.filterValue}
      loadMoreFilteredTimeline={
        !!props.filterValue && filteredTimeline?.canLoadMore ? loadMoreFilteredTimeline : undefined
      }
      loadingFilteredTimeline={!!props.filterValue && filteredTimeline?.isFetching}
    />
  );
}
